import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BulkSelectionProvider } from '@/contexts/BulkSelectionContext';
import { ProtectedRoute, PublicRoute, AdminRoute, AgentRoute } from '@/components/ProtectedRoute';
import { MainLayout, AuthLayout } from '@/components/layout';

// Authentication pages
import { Login, Register, ForgotPassword, ResetPassword } from '@/pages/auth';

// Dashboard pages
import { AdminDashboard, AgentDashboard, EndUserDashboard } from '@/pages/dashboard';

// Ticket pages
import { TicketList, TicketDetails, CreateTicket } from '@/pages/tickets';

// User pages
import { UserProfile, UserManagement, UserEdit } from '@/pages/users';

// Admin pages
import { Settings } from '@/pages/admin';
import { EmailManagement } from '@/pages/admin/EmailManagement';
import { EmailSettings } from '@/pages/admin/EmailSettings';
import { EmailNotificationHistory } from '@/pages/admin/EmailNotificationHistory';

// Analytics dashboards
import ExecutiveDashboard from '@/pages/analytics/ExecutiveDashboard';
import OperationalDashboard from '@/pages/analytics/OperationalDashboard';
import { AgentPerformance, ReportsOverview } from '@/pages/reports';

// Knowledge Base pages
import KnowledgeBase from '@/pages/KnowledgeBase';
import KnowledgeBaseAdmin from '@/pages/KnowledgeBaseAdmin';
import KnowledgeArticleView from '@/pages/KnowledgeArticleView';

// SLA Management pages
import { SLAPolicies } from '@/pages/sla/SLAPolicies';
import { SLADashboard } from '@/pages/sla/SLADashboard';
import { BusinessHoursCalendar } from '@/pages/sla/BusinessHoursCalendar';
import { SLABreachDashboard } from '@/pages/sla/SLABreachDashboard';

// Error pages
import { NotFound } from '@/pages/error/NotFound';
import { HelpCenter } from '@/pages/support/HelpCenter';
import { ContactSupport } from '@/pages/support/ContactSupport';

// Loading component
import { Spinner } from '@/components/Loading';

/**
 * App Router Component
 * 
 * Handles all routing for the application with role-based access control.
 * Includes protected routes, public routes, and proper layouts.
 */
export function AppRouter() {
  const { isAuthenticated, isInitialized, user, isAdmin, isAgent, isEndUser } = useAuth();

  // Show loading while auth is initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes - Only accessible when not authenticated */}
      <Route path="/login" element={
        <PublicRoute>
          <AuthLayout title="Sign In">
            <Login />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/register" element={
        <PublicRoute>
          <AuthLayout title="Create Account">
            <Register />
          </AuthLayout>
        </PublicRoute>
      } />
      {/* #43 Fix: Route for /users/:id – Matches /users/a65ccab1-c696-48a6-bd37-fc3694833153, renders UserProfile (no 404 message) */}
<Route path="/users/:id" element={
  <ProtectedRoute>
    <MainLayout>
      <UserProfile />
    </MainLayout>
  </ProtectedRoute>
} />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <AuthLayout title="Reset Password">
            <ForgotPassword />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/reset-password" element={
        <PublicRoute>
          <AuthLayout title="Set New Password">
            <ResetPassword />
          </AuthLayout>
        </PublicRoute>
      } />

      {/* Protected Routes - Require authentication */}
      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout>
            <Navigate to="/dashboard" replace />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Dashboard Routes - Role-based */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <DashboardRouter />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Ticket Routes - More specific routes first */}
      <Route path="/tickets/new" element={
        <ProtectedRoute>
          <MainLayout>
            <BulkSelectionProvider>
              <CreateTicket />
            </BulkSelectionProvider>
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/tickets/:ticketId/edit" element={
        <ProtectedRoute>
          <MainLayout>
            <BulkSelectionProvider>
              <TicketDetails />
            </BulkSelectionProvider>
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/tickets/:ticketId" element={
        <ProtectedRoute>
          <MainLayout>
            <BulkSelectionProvider>
              <TicketDetails />
            </BulkSelectionProvider>
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/tickets" element={
        <ProtectedRoute>
          <MainLayout>
            <BulkSelectionProvider>
              <TicketList />
            </BulkSelectionProvider>
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* User Profile Routes */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <MainLayout>
            <UserProfile />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Reports Routes - Accessible by Agents and Admins */}
      <Route path="/reports" element={
        <AgentRoute>
          <MainLayout>
            <Navigate to="/reports/overview" replace />
          </MainLayout>
        </AgentRoute>
      } />
      
      {/* Reports routes */}
      <Route path="/reports/overview" element={
        <AgentRoute>
          <MainLayout>
            <ReportsOverview />
          </MainLayout>
        </AgentRoute>
      } />
      
      <Route path="/reports/agents" element={
        <AdminRoute>
          <MainLayout>
            <AgentPerformance />
          </MainLayout>
        </AdminRoute>
      } />
      
      <Route path="/analytics/executive" element={
        <AdminRoute>
          <MainLayout>
            <ExecutiveDashboard />
          </MainLayout>
        </AdminRoute>
      } />
      
      <Route path="/analytics/operational" element={
        <AgentRoute>
          <MainLayout>
            <OperationalDashboard />
          </MainLayout>
        </AgentRoute>
      } />

      {/* User Management Routes */}
      <Route path="/users" element={
        <AdminRoute>
          <MainLayout>
            <UserManagement />
          </MainLayout>
        </AdminRoute>
      } />

      <Route path="/admin/users" element={
        <AdminRoute>
          <MainLayout>
            <UserManagement />
          </MainLayout>
        </AdminRoute>
      } />
      
      <Route path="/admin/users/:userId/edit" element={
        <AdminRoute>
          <MainLayout>
            <UserEdit />
          </MainLayout>
        </AdminRoute>
      } />
      
      <Route path="/settings" element={
        <AdminRoute>
          <MainLayout>
            <Settings />
          </MainLayout>
        </AdminRoute>
      } />
      
      <Route path="/admin/settings" element={
        <AdminRoute>
          <MainLayout>
            <Settings />
          </MainLayout>
        </AdminRoute>
      } />

      {/* Email Management Routes - Admin and Agent */}
      {/* BugHerd #158: this used to redirect unconditionally to /admin/email/settings, the
          one tab only an Admin can open, so Agents failed on entry. EmailManagement now
          resolves the landing tab from the signed-in role. */}
      <Route path="/admin/email" element={
        <AgentRoute>
          <MainLayout>
            <EmailManagement />
          </MainLayout>
        </AgentRoute>
      } />
      
      <Route path="/admin/email/settings" element={
        <AgentRoute>
          <MainLayout>
            <EmailManagement />
          </MainLayout>
        </AgentRoute>
      } />
      
      <Route path="/admin/email/history" element={
        <AgentRoute>
          <MainLayout>
            <EmailManagement />
          </MainLayout>
        </AgentRoute>
      } />

      <Route path="/admin/email/logs" element={
        <AgentRoute>
          <MainLayout>
            <EmailManagement />
          </MainLayout>
        </AgentRoute>
      } />

      {/* Knowledge Base Routes - Available to all authenticated users */}
      <Route path="/knowledge-base" element={
        <ProtectedRoute>
          <MainLayout>
            <KnowledgeBase />
          </MainLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/knowledge-base/admin" element={
        <AgentRoute>
          <MainLayout>
            <KnowledgeBaseAdmin />
          </MainLayout>
        </AgentRoute>
      } />

      <Route path="/knowledge-base/articles/:id" element={
        <ProtectedRoute>
          <MainLayout>
            <KnowledgeArticleView />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* SLA Management Routes - Accessible by Agents and Admins */}
      <Route path="/sla" element={
        <AgentRoute>
          <MainLayout>
            <Navigate to="/sla/dashboard" replace />
          </MainLayout>
        </AgentRoute>
      } />
      
      <Route path="/sla/dashboard" element={
        <AgentRoute>
          <MainLayout>
            <SLADashboard />
          </MainLayout>
        </AgentRoute>
      } />
      
      <Route path="/sla/policies" element={
        <AgentRoute>
          <MainLayout>
            <SLAPolicies />
          </MainLayout>
        </AgentRoute>
      } />

      <Route path="/sla/business-hours" element={
        <AgentRoute>
          <MainLayout>
            <BusinessHoursCalendar />
          </MainLayout>
        </AgentRoute>
      } />
      
      <Route path="/sla/breaches" element={
        <AgentRoute>
          <MainLayout>
            <SLABreachDashboard />
          </MainLayout>
        </AgentRoute>
      } />
      
      <Route path="/sla/approaching" element={
        <AgentRoute>
          <MainLayout>
            <SLABreachDashboard />
          </MainLayout>
        </AgentRoute>
      } />

      {/* General Routes */}
      <Route path="/help" element={
        <ProtectedRoute>
          <MainLayout>
            <HelpCenter />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/contact" element={
        <ProtectedRoute>
          <MainLayout>
            <ContactSupport />
          </MainLayout>
        </ProtectedRoute>
      } />

      {/* Catch all - 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/**
 * Dashboard Router Component
 * 
 * Routes to the appropriate dashboard based on user role
 */
function DashboardRouter() {
  const { user, isAdmin, isAgent, isEndUser, logout } = useAuth();

  useEffect(() => {
    if (user && !isAdmin() && !isAgent() && !isEndUser()) {
      console.error('Unknown user role, redirecting to login');
      logout();
    }
  }, [user, isAdmin, isAgent, isEndUser, logout]);

  if (!user) {
    return <div>Loading...</div>;
  }

  // Route to role-appropriate dashboard
  if (isAdmin()) {
    return <AdminDashboard />;
  }
  
  if (isAgent()) {
    return <AgentDashboard />;
  }
  
  if (isEndUser()) {
    return <EndUserDashboard />;
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Error</h2>
        <p className="text-gray-600 mb-4">Unable to determine user permissions. Please try logging in again.</p>
        <button 
          onClick={() => logout()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Return to Login
        </button>
      </div>
    </div>
  );
}
